import React, { useContext, useState } from 'react';
import { css } from '@emotion/react';
import { CircularProgress, TextField } from '@material-ui/core';
import { GreyBody, PrimaryButton, SectionSubtitle } from '@/styles/blocks';
import Snackbar from '@/components/Snackbar';
import ExternalIdentifierStore, { ExternalIdentifierContext } from './store';

const ExternalIdentifiersTab = ({ promptWhenEmpty }) => {
    const { state, callbacks } = useContext(ExternalIdentifierContext);
    const { rows, loading, busy, canSave, unavailable, hasStoredValues, valuesPending, valuesFailed } = state;
    const [prompted, setPrompted] = useState(false);

    if (loading && !rows.length) return <CircularProgress size={18} />;
    if (unavailable) return <GreyBody>No identifiers configured for your organization.</GreyBody>;
    if (valuesFailed) return <GreyBody>Couldn't load identifiers for this client. Refresh to try again.</GreyBody>;

    // Per the designer: the Add Details button "was meant to be there if no details exist. Can
    // be removed when data is filled in." Opt-in, so the modal tab keeps showing the form.
    if (promptWhenEmpty && valuesPending) return <CircularProgress size={18} />;
    if (promptWhenEmpty && !hasStoredValues && !prompted) {
        return (
            <div>
                <GreyBody
                    css={css`
                        display: block;
                        margin-bottom: 1rem;
                    `}
                >
                    No identifiers set for this client yet.
                </GreyBody>
                <PrimaryButton onClick={() => setPrompted(true)}>Add Details</PrimaryButton>
            </div>
        );
    }

    return (
        <div>
            {rows.map((row) => (
                <div
                    key={row.key}
                    css={css`
                        margin-bottom: 1.5rem;
                    `}
                >
                    <SectionSubtitle
                        css={css`
                            display: block;
                            margin-bottom: 0.5rem;
                        `}
                    >
                        {row.label}
                    </SectionSubtitle>
                    {row.description && (
                        <GreyBody
                            css={css`
                                display: block;
                                margin-bottom: 1rem;
                            `}
                        >
                            {row.description}
                        </GreyBody>
                    )}
                    <TextField
                        fullWidth
                        variant="outlined"
                        label={row.label}
                        value={row.value}
                        onChange={(e) => callbacks.onChange(row.key, e.target.value)}
                        disabled={loading || busy}
                    />
                </div>
            ))}
            <div>
                <PrimaryButton loading={busy} disabled={loading || !canSave} onClick={callbacks.save}>
                    Save
                </PrimaryButton>
            </div>
        </div>
    );
};

const Notification = () => {
    const { state, callbacks } = useContext(ExternalIdentifierContext);
    return (
        <Snackbar
            open={!!state.notification?.message}
            {...state.notification}
            handleClose={callbacks.dismissNotification}
            autohide={4000}
        />
    );
};

// Mounts its own store so the tab stays display-only. `promptWhenEmpty` (Company Info) shows an
// Add Details button instead of an empty form when nothing is stored.
const ExternalIdentifiers = ({ clientId, promptWhenEmpty }) => (
    <ExternalIdentifierStore clientId={clientId}>
        <Notification />
        <ExternalIdentifiersTab promptWhenEmpty={promptWhenEmpty} />
    </ExternalIdentifierStore>
);

export default ExternalIdentifiers;
